/**
 * Zona de arrastrar y soltar para un archivo.
 *
 * Sigue siendo un <input type="file"> por debajo: arrastrar es el atajo, no la
 * única vía. En caja se cobra con el celular en la mano y se arrastra la
 * captura de Yape desde el escritorio; pero quien usa teclado o lector de
 * pantalla necesita el input de siempre, y ahí está.
 */
import { useRef, useState, type DragEvent } from "react";
import { FileText, ImageIcon, UploadCloud, X } from "lucide-react";

export function DropZone({
  id,
  file,
  onFile,
  accept = "image/png,image/jpeg,application/pdf",
  hint = "PNG, JPG o PDF · hasta 5 MB",
}: {
  id: string;
  file: File | null;
  onFile: (f: File | null) => void;
  accept?: string;
  hint?: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [encima, setEncima] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  function tomar(f: File | null) {
    onFile(f);
    setPreview((anterior) => {
      if (anterior) URL.revokeObjectURL(anterior);
      return f && f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
    });
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setEncima(false);
    const f = e.dataTransfer.files?.[0];
    if (f) tomar(f);
  }

  if (file) {
    const Icono = file.type.startsWith("image/") ? ImageIcon : FileText;
    return (
      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        {preview ? (
          <img
            src={preview}
            alt=""
            className="h-12 w-12 shrink-0 rounded object-cover"
          />
        ) : (
          <Icono className="h-8 w-8 shrink-0 text-slate-400" />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">{file.name}</p>
          <p className="text-xs text-slate-500">
            {(file.size / 1024).toFixed(0)} KB
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            tomar(null);
            if (inputRef.current) inputRef.current.value = "";
          }}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
          aria-label="Quitar archivo"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setEncima(true);
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer rounded-lg border-2 border-dashed p-5 text-center transition ${
        encima
          ? "border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
          : "border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50"
      }`}
    >
      <UploadCloud
        className={`mx-auto mb-2 h-6 w-6 ${encima ? "text-[color:var(--color-link)]" : "text-slate-400"}`}
      />
      <p className="text-sm text-slate-600">
        Arrastra la captura aquí o{" "}
        <span className="font-medium text-[color:var(--color-link)] underline">búscala</span>
      </p>
      <p className="mt-0.5 text-xs text-slate-400">{hint}</p>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => tomar(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}
